import {
    StyledCardActions,
    StyledCardInfo,
    StyledCardInfoHeader,
    StyledCardLink,
    StyledCardRow,
    StyledContainerCard,
    SkeletonChip,
    SkeletonText,
    StyledBlurredContent
} from '@/features/measures/components/measure-card/style.ts'
import {Measure, SUB_TYPES} from '@/features/measures/services/types.ts'
import {MeasureType} from '@/features/measures/components/measure-type/MeasureType.tsx'
import {StyledOmissisBadge} from '@pages/measures/style.ts'
import {StyledLockIcon} from '@pages/measure-details/style.ts'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Chip} from '@components/ui/chip/Chip.tsx'
import {
    ArrowRightCircleIcon,
    CalendarIcon,
    EyeIcon,
    EyeOffIcon,
    FileTextIcon,
    MapPinIcon
} from '@components/ui/icon/Icon.tsx'
import dayjs from '@/dayjs.ts'
import {useTheme} from 'styled-components'
import {Button} from '@components/ui/button/Button.tsx'
import {useNavigate, useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import useWindowDimensions from '@hooks/useWindowDimensions.ts'
import {useEffect, useMemo, useState} from 'react'

import {copyToClipboard, formatGeneralRoleNumber} from '@utilities/helpers.ts'
import {BrowserEmbedded} from '@/features/measures/components/browser-embedded/BrowserEmbedded.tsx'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {useGetMeasureRawHtml} from '@/features/measure-details/services/useGetMeasureRawHtml.ts'
import {Tooltip} from '@components/ui/tooltip/Tooltip.tsx'
import {routes} from '@/utilities/constants/routes'
import {Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle} from '@/components/ui/modal-atoms/ModalAtoms'
import {ModalOverlay} from '@/components/ui/modal-atoms/ModalAtoms'
import {StyledBrowserEmbeddedContentParagraph} from '../browser-embedded/style'
import {toast} from 'react-hot-toast'

export const MeasureCard = ({
    measure,
    hasDetails = false,
    isLoading = false,
    setCurrentPage,
    setPerPage
}: {
    measure?: Measure
    hasDetails?: boolean
    isLoading?: boolean
    setCurrentPage?: (page: number) => void
    setPerPage?: (perPage: number) => void
}) => {
    const theme = useTheme()
    const navigate = useNavigate()
    const {state} = useLocation()
    const {t} = useTranslation()
    const {width} = useWindowDimensions()
    const isMobile = useMemo(() => width < 768, [width])

    const [measureContent, setMeasureContent] = useState<string | null>(null)
    const [loadingMeasures, setLoadingMeasures] = useState<Record<string, boolean>>({})
    const [showOmissisContent, setShowOmissisContent] = useState<Record<string, boolean>>({})
    const [showOmissisWarning, setShowOmissisWarning] = useState<Record<string, boolean>>({})

    const measureQuery = useGetMeasureRawHtml()

    const handleFetchMeasure = (id: string) => {
        setLoadingMeasures(prev => ({...prev, [id]: true}))

        measureQuery.mutate(id, {
            onSuccess: data => {
                setMeasureContent(data?.data)
            },
            onSettled: () => {
                setLoadingMeasures(prev => ({...prev, [id]: false}))
            }
        })
    }

    const handleConfirmView = (measureId: string) => {
        setShowOmissisWarning(prev => ({...prev, [measureId]: false}))
        setShowOmissisContent(prev => ({...prev, [measureId]: true}))
    }

    const handleCancelView = (measureId: string) => {
        setShowOmissisWarning(prev => ({...prev, [measureId]: false}))
    }

    const handleToggleOmissisContent = (measureId: string) => {
        if (!showOmissisContent[measureId]) {
            setShowOmissisWarning(prev => ({...prev, [measureId]: true}))
        } else {
            setShowOmissisContent(prev => ({...prev, [measureId]: false}))
        }
    }

    useEffect(() => {
        if (state?.page && setCurrentPage) {
            setCurrentPage(state.page)
        }

        if (state?.perPage && setPerPage) {
            setPerPage(state.perPage)
        }
    }, [state?.page, state?.perPage, setCurrentPage, setPerPage])

    const handleCopy = (textToCopy: any) => {
        if (!textToCopy) {
            toast.error(t('commons:copy_failed'))
        }

        copyToClipboard(textToCopy)
        toast.success(t('commons:copy_success'))
    }

    if (isLoading) {
        return (
            <StyledContainerCard direction={'column'} gap={4} justify={'end'} align={'flex-start'}>
                <SkeletonText style={{width: '100px'}} />
                <Flexbox align={'center'} gap={2}>
                    <SkeletonChip />
                    <SkeletonText style={{width: '100px'}} />
                </Flexbox>
                <Flexbox align={'center'} gap={2}>
                    <SkeletonChip />
                    <SkeletonText style={{width: '100px'}} />
                </Flexbox>

                <StyledCardRow>
                    <Flexbox align={'center'} gap={3}>
                        <SkeletonChip />
                        <SkeletonText style={{width: '120px'}} />
                    </Flexbox>
                </StyledCardRow>

                <StyledCardRow>
                    <Flexbox
                        direction={isMobile ? 'column' : 'row'}
                        gap={4}
                        justify={isMobile ? 'flex-start' : 'space-between'}
                        align={isMobile ? 'flex-start' : 'center'}
                    >
                        <SkeletonText style={{width: '150px'}} />
                        <SkeletonText style={{width: '100px'}} />
                    </Flexbox>

                    {hasDetails && (
                        <Flexbox direction={'column'} gap={4}>
                            <SkeletonText style={{width: '150px'}} />
                            <SkeletonText style={{width: '200px'}} />
                        </Flexbox>
                    )}
                </StyledCardRow>

                <StyledCardActions gap={3} align={'center'}>
                    <SkeletonText style={{width: '120px', height: '32px'}} />
                    <SkeletonText style={{width: '120px', height: '32px'}} />
                </StyledCardActions>
            </StyledContainerCard>
        )
    }

    if (!measure) return null

    return (
        <>
            <StyledContainerCard
                direction={'column'}
                gap={4}
                justify={isMobile ? 'center' : 'space-between'}
                align={'flex-start'}
            >
                <Flexbox align={'center'} gap={2}>
                    <MeasureType type={measure?.subType as SUB_TYPES} />
                    {measure?.isOmissis && (
                        <Tooltip content={t('measures:table:omissis_tooltip')}>
                            <StyledOmissisBadge align={'center'}>
                                <StyledLockIcon />
                            </StyledOmissisBadge>
                        </Tooltip>
                    )}
                </Flexbox>

                <StyledCardRow>
                    <Flexbox align={'center'} gap={3}>
                        <Chip icon={<MapPinIcon />} />
                        <StyledCardInfo>
                            {measure?.headquarterLocation?.keyName.replace(/_/g, ' ') || '-'}
                        </StyledCardInfo>
                    </Flexbox>

                    <Flexbox align={'center'} gap={3}>
                        <Chip
                            icon={<CalendarIcon />}
                            textColor={theme.palette.success['600']}
                            bgColor={theme.palette.success['100']}
                        />
                        <StyledCardInfo>
                            {measure?.publishedAt ? dayjs(measure?.publishedAt).format('DD/MM/YYYY') : '-'}
                        </StyledCardInfo>
                    </Flexbox>
                </StyledCardRow>

                <StyledCardRow>
                    <Flexbox
                        direction={isMobile ? 'column' : 'row'}
                        gap={4}
                        justify={isMobile ? 'flex-start' : 'space-between'}
                        align={isMobile ? 'flex-start' : 'center'}
                    >
                        <StyledCardInfoHeader>{t('measures:table:measure_number')}</StyledCardInfoHeader>
                        <StyledCardInfo style={{cursor: 'pointer'}} onClick={() => handleCopy(measure?.measureNumber)}>
                            {String(measure?.measureNumber || '-')}
                        </StyledCardInfo>
                    </Flexbox>

                    <Flexbox
                        direction={isMobile ? 'column' : 'row'}
                        gap={4}
                        justify={isMobile ? 'flex-start' : 'space-between'}
                        align={isMobile ? 'flex-start' : 'center'}
                    >
                        <StyledCardInfoHeader>{t('measures:table:general_role_number')}</StyledCardInfoHeader>
                        {!measure?.appealMeasureId ? (
                            <StyledCardInfo
                                style={{cursor: 'pointer'}}
                                onClick={() => handleCopy(formatGeneralRoleNumber(measure?.appealNumber?.toString()))}
                            >
                                {formatGeneralRoleNumber(measure?.appealNumber?.toString()) ?? '-'}
                            </StyledCardInfo>
                        ) : (
                            <StyledCardLink
                                href={`${routes.MEASURE_DETAILS.path.replace(':id', measure?.appealMeasureId)}`}
                                target={'_blank'}
                            >
                                <span>{formatGeneralRoleNumber(measure?.appealNumber?.toString()) ?? '-'}</span>
                            </StyledCardLink>
                        )}
                    </Flexbox>

                    {hasDetails && (
                        <Flexbox direction={'column'} gap={4} data-highlight="true">
                            <Flexbox justify={'flex-start'} align={'center'} fullWidth gap={2}>
                                <StyledCardInfoHeader>{t('measures:table:highlight_detail')}</StyledCardInfoHeader>
                                {measure?.isOmissis && (
                                    <Button
                                        variant={'ghost'}
                                        size={'sm'}
                                        onClick={() => handleToggleOmissisContent(measure.id)}
                                    >
                                        {showOmissisContent[measure.id] ? (
                                            <EyeOffIcon size={16} fill={theme.palette.primary['600']} />
                                        ) : (
                                            <EyeIcon size={16} fill={theme.palette.primary['600']} />
                                        )}
                                    </Button>
                                )}
                            </Flexbox>
                            <StyledBlurredContent $isBlurred={measure?.isOmissis && !showOmissisContent[measure.id]}>
                                <StyledCardInfo
                                    dangerouslySetInnerHTML={{
                                        __html: measure?.highlight || '-'
                                    }}
                                />
                            </StyledBlurredContent>
                        </Flexbox>
                    )}
                </StyledCardRow>

                <StyledCardActions gap={3} align={'center'}>
                    <Button variant={'primaryLight'} onClick={() => handleFetchMeasure(measure.id)}>
                        {loadingMeasures[measure.id] ? <Spinner size={10} /> : <FileTextIcon size={20} />}
                    </Button>
                    <Button
                        variant={'primaryLight'}
                        onClick={() =>
                            navigate(`/measure/${measure.id}`, {
                                state: {
                                    from: `${location.pathname}${location.search}`
                                }
                            })
                        }
                    >
                        <ArrowRightCircleIcon size={20} />
                    </Button>
                </StyledCardActions>
            </StyledContainerCard>

            {measureContent && (
                <BrowserEmbedded
                    isOpen={Boolean(measureContent)}
                    onClose={() => setMeasureContent(null)}
                    htmlContent={measureContent}
                    isOmissis={measure.isOmissis}
                    measureType={measure.subType?.toLowerCase()}
                />
            )}

            {measure?.isOmissis && showOmissisWarning[measure.id] && (
                <>
                    <ModalOverlay />
                    <Modal width="400px">
                        <ModalHeader>
                            <ModalTitle>{t('measures:omissis_warning:title')}</ModalTitle>
                        </ModalHeader>
                        <ModalBody>
                            <StyledBrowserEmbeddedContentParagraph>
                                {t('measures:omissis_warning:message')}
                            </StyledBrowserEmbeddedContentParagraph>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" onClick={() => handleCancelView(measure.id)}>
                                {t('commons:cancel')}
                            </Button>
                            <Button onClick={() => handleConfirmView(measure.id)}>
                                {t('measures:omissis_warning:confirm')}
                            </Button>
                        </ModalFooter>
                    </Modal>
                </>
            )}
        </>
    )
}
